/**
 * "Needs My Approval" for 213RR lists.
 *
 * The approval chain is Section Chief / Command, then RESL, Logistics and
 * Finance. The pending step is the first one without a signature
 * (`getCurrentStage`); a request needs the user's approval when a seat they
 * hold is the one that signs that step.
 */

import type { ResourceRequest } from 'components/IAP/hooks/incidentResourceRequestHooks';
import type { ApprovalStage } from 'components/IAP/ResourceRequests/utils/ResourceRequestTypes';
import { getCurrentStage } from 'components/IAP/ResourceRequests/utils/ResourceRequestUtils';

export type ApprovalStep = {
  stage: Extract<
    ApprovalStage,
    'section_chief' | 'resl' | 'logistics' | 'finance'
  >;
  /** Exact roster names, matching the form body's own seat checks. */
  seatNames: readonly string[];
  /** Case-insensitive substrings for Section Chief / Command Staff seats. */
  seatSubstrings: readonly string[];
};

export const APPROVAL_STEPS: readonly ApprovalStep[] = [
  {
    stage: 'section_chief',
    seatNames: [],
    seatSubstrings: [
      'section chief',
      'incident commander',
      'unified command',
      'officer',
    ],
  },
  { stage: 'resl', seatNames: ['Resource Unit Leader'], seatSubstrings: [] },
  {
    stage: 'logistics',
    seatNames: ['Logistics Section Chief'],
    seatSubstrings: [],
  },
  {
    stage: 'finance',
    seatNames: ['Finance Section Chief'],
    seatSubstrings: [],
  },
];

type MemberPosition = { positionName: string };

export function approvalStepForRequest(
  request: Pick<
    ResourceRequest,
    | 'status'
    | 'requestor_signature'
    | 'section_chief_signature'
    | 'resl_signature'
    | 'logistics_signature'
    | 'finance_signature'
  >
): ApprovalStep | null {
  if (request.status === 'cancelled') return null;
  const stage = getCurrentStage(request.status, request as ResourceRequest);
  return APPROVAL_STEPS.find((step) => step.stage === stage) ?? null;
}

export function positionsCanSignStep(
  step: ApprovalStep,
  memberPositions: readonly MemberPosition[]
): boolean {
  return memberPositions.some((position) => {
    const name = position.positionName ?? '';
    if (step.seatNames.includes(name)) return true;
    const lower = name.trim().toLowerCase();
    return (
      !!lower && step.seatSubstrings.some((needle) => lower.includes(needle))
    );
  });
}

/** True when the request is waiting on a step one of these seats signs. */
export function requestNeedsMyApproval(
  request: Parameters<typeof approvalStepForRequest>[0],
  memberPositions: readonly MemberPosition[]
): boolean {
  const step = approvalStepForRequest(request);
  return !!step && positionsCanSignStep(step, memberPositions);
}
